import { useState, type FormEvent } from "react";
import {
  PRESET_MAX,
  PRESET_NAME_MAX,
  sameFilters,
  upsertPreset,
  useFilterPresets,
} from "@/lib/filterPresets";

type Props<T> = {
  /** Storage namespace for this list's presets, e.g. {@code "prospects"}. */
  storageKey: string;
  /** The page's current dropdown selections. */
  current: T;
  /** The page's reviver for a stored filter payload. */
  revive: (stored: unknown) => T | null;
  /** Called with a preset's filters when the user picks it. */
  onApply: (filters: T) => void;
};

const SELECT_CLASS =
  "rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white";
const BUTTON_CLASS =
  "rounded-md border border-[var(--border)] bg-white px-3 py-1.5 text-sm hover:bg-zinc-50";

/**
 * "Saved filters" for a list page: pick a preset to apply it, save the current
 * dropdowns under a name, delete the one that is selected.
 *
 * <p>The dropdown shows a preset as selected only while the page's filters
 * equal it exactly. Change any dropdown afterwards and it reads "Custom"
 * again; the preset itself is never edited implicitly — saving under the same
 * name is how it gets updated.
 *
 * <p>Presets hold dropdown selections only. The page decides what goes into
 * {@code current}; free-text search never does, because it can hold prospect
 * data and presets live in the viewer's browser storage.
 */
export default function FilterPresets<T extends Record<string, unknown>>({
  storageKey,
  current,
  revive,
  onApply,
}: Props<T>) {
  const { presets, save, remove } = useFilterPresets<T>(storageKey, revive);
  const selected = presets.find((p) => sameFilters(p.filters, current)) ?? null;

  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);

  function openForm() {
    setName(selected?.name ?? "");
    setRefusal(null);
    setNaming(true);
  }

  function closeForm() {
    setNaming(false);
    setName("");
    setRefusal(null);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim() === "") {
      setRefusal("Give the filter a name.");
      return;
    }
    // Explain a refused save in the user's terms before touching storage.
    const wouldBeNew = upsertPreset(presets, name, current) === presets;
    if (wouldBeNew && presets.length >= PRESET_MAX) {
      setRefusal(`Limit of ${PRESET_MAX} saved filters reached. Delete one first.`);
      return;
    }
    if (save(name, current)) closeForm();
  }

  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="text-sm">
        <div className="text-xs text-[var(--fg-muted)] mb-1">Saved filters</div>
        <select
          value={selected?.id ?? ""}
          onChange={(e) => {
            const preset = presets.find((p) => p.id === e.target.value);
            if (preset) onApply(preset.filters);
          }}
          className={SELECT_CLASS}
        >
          <option value="">{presets.length > 0 ? "Custom" : "None saved"}</option>
          {presets.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </label>

      {naming ? (
        <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="text-xs text-[var(--fg-muted)] mb-1">Filter name</div>
            <input
              autoFocus
              value={name}
              maxLength={PRESET_NAME_MAX}
              onChange={(e) => { setName(e.target.value); setRefusal(null); }}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm w-48"
            />
          </label>
          <button type="submit" className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700">
            Save
          </button>
          <button type="button" onClick={closeForm} className={BUTTON_CLASS}>
            Cancel
          </button>
          <span className="text-xs text-[var(--fg-muted)] pb-2">
            {refusal ?? "Dropdowns only; search text is not saved."}
          </span>
        </form>
      ) : (
        <>
          <button type="button" onClick={openForm} className={BUTTON_CLASS}>
            Save filters…
          </button>
          {selected ? (
            <button
              type="button"
              aria-label="Delete saved filter"
              onClick={() => remove(selected.id)}
              className={BUTTON_CLASS}
            >
              Delete
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
